import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Card, CardLabel } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { MAX_RANGE_DAYS, type AnalyticsPreset, type AnalyticsSelection } from "@/lib/analytics";

export const CHART_COLORS = [
  "hsl(42 80% 57%)",
  "hsl(150 54% 51%)",
  "hsl(200 70% 50%)",
  "hsl(330 70% 55%)",
  "hsl(270 60% 60%)",
  "hsl(0 0% 55%)",
] as const;

export function chartColor(i: number): string {
  return CHART_COLORS[i % CHART_COLORS.length]!;
}

export function fmt(n: number): string {
  return n.toLocaleString("pt-BR");
}

export function EmptyChart({ message = "Sem dados no período selecionado." }: { message?: string }): JSX.Element {
  return (
    <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-[hsl(var(--border-strong))] bg-[hsl(var(--surface)/0.45)] px-4 text-center">
      <p className="text-xs leading-relaxed text-muted-foreground">{message}</p>
    </div>
  );
}

export function ChartSkeleton(): JSX.Element {
  return (
    <div className="animate-pulse" aria-busy="true">
      <div className="h-32 w-full rounded bg-[hsl(var(--border-strong)/0.3)]" />
    </div>
  );
}

interface SectionCardProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}

export function SectionCard({ title, subtitle, icon, children, className }: SectionCardProps): JSX.Element {
  return (
    <Card variant="raised" className={cn("p-5 flex flex-col gap-4 min-w-0", className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <CardLabel>{title}</CardLabel>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {icon && <span className="text-muted-foreground shrink-0">{icon}</span>}
      </div>
      {children}
    </Card>
  );
}

interface MetricCardProps {
  readonly label: string;
  readonly value: string;
  readonly icon: ReactNode;
  readonly subtitle?: string;
  readonly isLoading?: boolean;
}

export function MetricCard({ label, value, icon, subtitle, isLoading }: MetricCardProps): JSX.Element {
  return (
    <Card variant="raised" className="p-4 flex flex-col gap-2">
      {isLoading ? (
        <div className="animate-pulse space-y-3" aria-busy="true">
          <div className="h-2.5 w-20 rounded bg-[hsl(var(--border-strong))]" />
          <div className="h-8 w-16 rounded bg-[hsl(var(--border-strong))]" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <CardLabel>{label}</CardLabel>
            <div className="h-7 w-7 rounded-lg bg-[hsl(var(--primary)/0.1)] flex items-center justify-center text-[hsl(var(--primary))]">
              {icon}
            </div>
          </div>
          <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{value}</p>
          {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
        </>
      )}
    </Card>
  );
}

const PRESET_LABELS: Record<AnalyticsPreset, string> = {
  today: "Hoje",
  "7d": "7 dias",
  "30d": "30 dias",
  custom: "Personalizado",
};

interface PeriodPickerProps {
  readonly value: AnalyticsSelection;
  readonly onChange: (next: AnalyticsSelection) => void;
  readonly invalid?: boolean;
}

/** Hoje / 7 dias / 30 dias / Personalizado (ate MAX_RANGE_DAYS dias). */
export function PeriodPicker({ value, onChange, invalid }: PeriodPickerProps): JSX.Element {
  const presets: AnalyticsPreset[] = ["today", "7d", "30d", "custom"];
  return (
    <div className="flex flex-col items-end gap-2">
      <div role="group" aria-label="Selecionar período" className="flex flex-wrap items-center rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-0.5 gap-0.5">
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={value.preset === p}
            onClick={() => onChange({ ...value, preset: p })}
            className={cn(
              "rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]",
              value.preset === p
                ? "bg-[hsl(var(--surface-raised))] text-foreground shadow-sm border border-[hsl(var(--border-strong))]"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {PRESET_LABELS[p]}
          </button>
        ))}
      </div>
      {value.preset === "custom" && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1 text-muted-foreground">
            De
            <input type="date" aria-label="Data inicial" value={value.from ?? ""} onChange={(e) => onChange({ ...value, from: e.target.value })} className="rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface))] px-2 py-1 text-foreground" />
          </label>
          <label className="flex items-center gap-1 text-muted-foreground">
            até
            <input type="date" aria-label="Data final" value={value.to ?? ""} onChange={(e) => onChange({ ...value, to: e.target.value })} className="rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface))] px-2 py-1 text-foreground" />
          </label>
          {invalid && <span role="alert" className="text-[hsl(var(--destructive))]">Escolha um intervalo válido de até {MAX_RANGE_DAYS} dias.</span>}
        </div>
      )}
    </div>
  );
}

/** Abas Visao geral / Membros e inscritos. */
export function AnalyticsTabs({ active }: { readonly active: "overview" | "members" }): JSX.Element {
  const tabs = [
    { key: "overview", to: "/app/analytics", label: "Visão geral" },
    { key: "members", to: "/app/analytics/membros", label: "Membros e inscritos" },
  ] as const;
  return (
    <nav aria-label="Seções do Analytics" className="flex gap-1 border-b border-[hsl(var(--border))]">
      {tabs.map((t) => (
        <Link
          key={t.key}
          to={t.to}
          aria-current={active === t.key ? "page" : undefined}
          className={cn(
            "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
            active === t.key ? "border-[hsl(var(--primary))] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          {t.label}
        </Link>
      ))}
    </nav>
  );
}
